/**
 * shell/sidebar.tsx
 * SK Hire left navigation — premium, animated. Logo block, WORKSPACE section,
 * five nav sections with an animated active indicator + hover lift, an
 * AI-credits meter, and the signed-in user block with a premium hover card
 * (name · email · role · org).
 *
 * Widths: 238px docked · 64px icon-rail (compact) · full-bleed in the mobile
 * overlay drawer. No topbar by design — page headers live inside each screen.
 */
import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useSession, roleLabel } from '../context/sessionContext';
import { useActiveView } from '../navigation/useActiveView';
import { navigateApp } from '../navigation/navigationRef';
import { Icon, ProgressMeter } from '../components/ui';
import Switch from '../components/switch';
import { useHover } from '../hooks/useHover';
import { useWorkspaceStats, type AiUsage } from '../hooks/useAiUsage';
import { web } from '../utils/web';
import type { IconName } from '../components/icon';
import type { NavSection } from '../navigation/types';

interface NavItem {
  key: NavSection;
  label: string;
  icon: IconName;
}

const NAV: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: 'grid' },
  { key: 'jobs', label: 'Jobs', icon: 'layers' },
  { key: 'candidates', label: 'Candidates', icon: 'users' },
  { key: 'integrations', label: 'Integrations', icon: 'link' },
  { key: 'compliance', label: 'Compliance', icon: 'shield' },
];

// Softknack-internal Ops console — only surfaced to admin/owner roles
// (the API enforces hire:admin regardless).
const OPS_NAV: NavItem = { key: 'ops', label: 'Ops', icon: 'zap' };
function isAdminRole(role?: string | null): boolean {
  return !!role && /admin|owner/i.test(role);
}

interface SidebarProps {
  onLogout?: () => void;
  compact?: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'SK';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

const IS_WEB = Platform.OS === 'web';

/** Eased count-up 0 → target; re-runs each time `active` flips true (web only). */
function useCountUp(target: number, active: boolean, duration = 900): number {
  const [n, setN] = useState(active && IS_WEB ? 0 : target);
  useEffect(() => {
    if (!IS_WEB) { setN(target); return; }
    if (!active) { setN(0); return; }
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 4);
      setN(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return n;
}

function NavRow({ item, active, compact, badge, onPress }: { item: NavItem; active: boolean; compact: boolean; badge?: number; onPress: () => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const fg = active ? colors.accentInk : hovered ? colors.ink : colors.ink2;

  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      style={[
        styles.navRow,
        compact && styles.navRowCompact,
        {
          borderRadius: radii.md,
          backgroundColor: active ? colors.accentSoft : hovered ? colors.paper2 : 'transparent',
        },
        web({ transition: 'background 160ms ease, transform 160ms ease', cursor: 'pointer' }),
        !active && hovered && web({ transform: 'translateX(2px)' }),
      ]}
    >
      {/* Animated active indicator rail */}
      <View
        style={[
          styles.activeBar,
          compact && { left: 6 },
          {
            backgroundColor: colors.accent,
            opacity: active ? 1 : 0,
            transform: [{ scaleY: active ? 1 : 0.3 }],
          },
          web({ transition: 'opacity 200ms ease, transform 200ms ease' }),
          Platform.OS === 'web' && active ? ({ boxShadow: `0 0 8px -1px ${colors.accent}` } as object) : null,
        ]}
      />
      <View
        style={[
          styles.navIcon,
          {
            backgroundColor: active ? colors.card : 'transparent',
            borderRadius: radii.sm,
          },
          web({ transition: 'background 160ms ease' }),
          Platform.OS === 'web' && active ? ({ boxShadow: '0 1px 2px rgba(0,0,0,0.06)' } as object) : null,
        ]}
      >
        <Icon name={item.icon} size={16} color={fg} />
      </View>
      {!compact && (
        <>
          <Text style={{ flex: 1, fontSize: fontSize.md, color: fg, fontWeight: active ? fontWeight.semibold : fontWeight.medium }}>
            {item.label}
          </Text>
          {badge != null && badge > 0 && (
            <View style={{ minWidth: 20, paddingHorizontal: 6, height: 18, borderRadius: radii.pill, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.card : colors.paper3 }}>
              <Text style={{ fontFamily: font.mono, fontSize: 10, fontWeight: fontWeight.semibold, color: active ? colors.accentInk : colors.ink3 }}>
                {badge}
              </Text>
            </View>
          )}
        </>
      )}
    </Pressable>
  );
}

/** Sidebar action row (appearance toggle + log out) — rendered inside the nav
 *  list directly under Ops, so it matches the nav-row shape exactly. `danger`
 *  tints the label/icon on hover (Log out); `compact` = icon-only rail. */
function SideActionRow({
  icon, label, onPress, trailing, danger, compact = false,
}: { icon: IconName; label: string; onPress?: () => void; trailing?: React.ReactNode; danger?: boolean; compact?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const fg = danger ? (hovered ? colors.danger : colors.ink2) : hovered ? colors.ink : colors.ink2;
  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      style={[
        styles.navRow,
        compact && styles.navRowCompact,
        { borderRadius: radii.md, backgroundColor: hovered ? colors.paper2 : 'transparent' },
        web({ cursor: 'pointer', transition: 'background 160ms ease, transform 160ms ease' }),
        !compact && hovered && web({ transform: 'translateX(2px)' }),
      ]}
    >
      <View style={[styles.navIcon, { borderRadius: radii.sm }]}>
        <Icon name={icon} size={16} color={fg} />
      </View>
      {!compact && (
        <>
          <Text style={{ flex: 1, fontSize: fontSize.md, color: fg, fontWeight: fontWeight.medium }}>{label}</Text>
          {trailing}
        </>
      )}
    </Pressable>
  );
}

/** Premium log-out button inside the profile card — outlined, hover tints danger. */
function CardLogoutButton({ onPress }: { onPress?: () => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      accessibilityLabel="Log out"
      style={[
        {
          flexDirection: 'row', alignItems: 'center', gap: 7, paddingVertical: 8, paddingHorizontal: 12,
          borderRadius: radii.md, borderWidth: 1,
          borderColor: hovered ? colors.dangerLine : colors.line,
          backgroundColor: hovered ? colors.dangerSoft : colors.card,
        },
        web({ cursor: 'pointer', transition: 'background 150ms ease, border-color 150ms ease, transform 150ms ease' }),
        hovered && web({ transform: 'translateY(-1px)' }),
      ]}
    >
      <Icon name="logout" size={14} color={hovered ? colors.danger : colors.ink2} />
      <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: hovered ? colors.danger : colors.ink2 }}>Log out</Text>
    </Pressable>
  );
}

/** Premium hover card floating above the user block: avatar · name · email · role · org · log out. */
function ProfileHoverCard({ visible, name, email, role, org, onLogout }: { visible: boolean; name: string; email: string; role: string; org: string; onLogout?: () => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  return (
    <View
      pointerEvents={visible ? 'auto' : 'none'}
      style={[
        styles.hoverCard,
        {
          backgroundColor: colors.card,
          borderColor: colors.line,
          borderRadius: radii.lg,
          opacity: visible ? 1 : 0,
          transform: [{ translateY: visible ? 0 : 8 }],
        },
        shadows.lg as object,
        web({ transition: 'opacity 180ms ease, transform 180ms ease' }),
      ]}
    >
      {/* Invisible hover bridge over the gap so moving the cursor up into the
          card (to click Log out) never dismisses it. */}
      <View style={{ position: 'absolute', top: '100%', left: 0, right: 0, height: 14 }} />

      {/* Header: big avatar + name */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11 }}>
        <View
          style={[
            styles.hoverAvatar,
            { borderRadius: radii.md },
            Platform.OS === 'web'
              ? ({ backgroundImage: `linear-gradient(135deg, ${colors.accent}, ${colors.accentInk})` } as object)
              : { backgroundColor: colors.accent },
          ]}
        >
          <Text style={{ color: colors.onAccent, fontWeight: fontWeight.bold, fontSize: 16, letterSpacing: -0.3 }}>{initials(name)}</Text>
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text numberOfLines={2} style={{ fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.2 }}>{name}</Text>
          <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 }} {...(Platform.OS === 'web' ? ({ selectable: true } as object) : {})}>{email || 'No email on file'}</Text>
        </View>
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 11 }} />

      {/* Role + org rows */}
      <View style={{ gap: 7 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Icon name="shield" size={13} color={colors.ink4} />
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textTransform: 'capitalize' }}>{role || 'Member'}</Text>
        </View>
        {!!org && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Icon name="grid" size={13} color={colors.ink4} />
            <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink2 }}>{org}</Text>
          </View>
        )}
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 11 }} />

      {/* Signed-in status + log out */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
          <View style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: colors.success }} />
          <Text style={{ fontSize: 10, color: colors.ink4, letterSpacing: 0.3 }}>Signed in</Text>
        </View>
        <CardLogoutButton onPress={onLogout} />
      </View>
    </View>
  );
}

/** One legend row inside the credits card: tone dot · label · value (· pct). */
function UsageLegendRow({ tone, label, value, pct }: { tone: string; label: string; value: string; pct?: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <View style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: tone }} />
      <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink2 }}>{label}</Text>
      <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>{value}</Text>
      {pct != null && <Text style={{ fontFamily: font.mono, fontSize: 10, color: colors.ink4, width: 34, textAlign: 'right' }}>{pct}%</Text>}
    </View>
  );
}

/** Premium hover card for the AI-credits block — floats above it with a full
 *  usage breakdown: big total, an animated input/output split bar (grows +
 *  sheens on show) and a per-bucket legend. Live over the same SSE stream. */
function CreditsHoverCard({ visible, usage, mobile = false }: { visible: boolean; usage: AiUsage; mobile?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii, shadows } = theme;
  const total = usage.totalTokens;
  const inPct = total > 0 ? Math.round((usage.inputTokens / total) * 100) : 0;
  const outPct = total > 0 ? 100 - inPct : 0;
  const totalUp = useCountUp(total, visible);
  return (
    <View
      pointerEvents={visible ? 'auto' : 'none'}
      style={[
        // Desktop: opens to the right of the rail. Mobile drawer: opens ABOVE the
        // card and spans the drawer width (left:0/right:0) so it never overflows.
        mobile
          ? { position: 'absolute', left: 0, right: 0, bottom: '100%', marginBottom: 10, borderWidth: 1, padding: 16, zIndex: 60 }
          : styles.creditsFlyout,
        {
          backgroundColor: colors.card,
          borderColor: visible ? colors.lineStrong : colors.line,
          borderRadius: radii.lg,
          opacity: visible ? 1 : 0,
          // Slide in — up from the card on mobile, out from the rail on desktop.
          transform: mobile
            ? [{ translateY: visible ? 0 : 12 }, { scale: visible ? 1 : 0.98 }]
            : [{ translateX: visible ? 0 : -14 }, { scale: visible ? 1 : 0.97 }],
        },
        shadows.lg as object,
        // Premium depth: a soft ambient glow + hairline ring on show.
        IS_WEB
          ? ({
              boxShadow: visible
                ? `0 24px 60px -24px rgba(0,0,0,0.5), 0 2px 8px -4px rgba(0,0,0,0.3), 0 0 0 1px ${colors.line}`
                : '0 0 0 1px transparent',
              transition: 'opacity 260ms cubic-bezier(.2,.8,.2,1), transform 320ms cubic-bezier(.2,.9,.2,1), box-shadow 260ms ease, border-color 260ms ease',
            } as object)
          : null,
      ]}
    >
      {/* Hover bridge over the gap so moving the cursor from the card to the
          flyout never dismisses it — below the card on mobile (flyout is above),
          to the left on desktop (flyout is to the right of the rail). */}
      {mobile
        ? <View style={{ position: 'absolute', top: '100%', left: 0, right: 0, height: 12 }} />
        : <View style={{ position: 'absolute', right: '100%', top: 0, bottom: 0, width: 18 }} />}

      {/* Premium gradient accent strip along the top edge. */}
      {IS_WEB ? (
        <View
          style={{
            position: 'absolute', top: 0, left: 16, right: 16, height: 2, borderRadius: 2,
            ...({ backgroundImage: `linear-gradient(90deg, transparent, ${colors.accent}, ${colors.teal}, transparent)` } as object),
          }}
        />
      ) : null}

      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
          <View
            style={[
              styles.creditsGlyph,
              { borderRadius: radii.md, backgroundColor: colors.accent },
              Platform.OS === 'web' ? ({ backgroundImage: `linear-gradient(135deg, ${colors.accent}, ${colors.accentInk})` } as object) : null,
            ]}
          >
            <Icon name="zap" size={15} color={colors.onAccent} />
          </View>
          <View>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>AI usage</Text>
            <Text style={{ fontSize: 10, color: colors.ink4, letterSpacing: 0.3 }}>Prepaid credits · SK Meter</Text>
          </View>
        </View>
        <View style={{ paddingHorizontal: 8, height: 20, borderRadius: 999, borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 10, fontWeight: fontWeight.semibold, color: colors.accentInk }}>Metered</Text>
        </View>
      </View>

      {/* Big total */}
      <View style={{ marginTop: 14, flexDirection: 'row', alignItems: 'flex-end', gap: 7 }}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize['3xl'], fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.5 }}>
          {totalUp.toLocaleString()}
        </Text>
        <Text style={{ fontSize: fontSize.sm, color: colors.ink4, marginBottom: 5 }}>tokens used</Text>
      </View>

      {/* Animated split bar — grows from 0 on show, with a one-shot sheen. */}
      <View style={{ height: 8, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden', flexDirection: 'row', marginTop: 10, position: 'relative' }}>
        <View style={[{ width: `${visible ? inPct : 0}%`, backgroundColor: colors.accent }, web({ transition: 'width 700ms cubic-bezier(.2,.8,.2,1)' })]} />
        <View style={[{ width: `${visible ? outPct : 0}%`, backgroundColor: colors.teal }, web({ transition: 'width 700ms cubic-bezier(.2,.8,.2,1) 60ms' })]} />
        {visible && Platform.OS === 'web' ? (
          <View style={{ position: 'absolute', top: 0, bottom: 0, width: 40, ...({ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', animation: 'sk-sheen 900ms ease-out 200ms 1' } as object) }} />
        ) : null}
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 13 }} />

      {/* Per-bucket legend */}
      <View style={{ gap: 9 }}>
        <UsageLegendRow tone={colors.accent} label="Input" value={usage.inputTokens.toLocaleString()} pct={inPct} />
        <UsageLegendRow tone={colors.teal} label="Output" value={usage.outputTokens.toLocaleString()} pct={outPct} />
        <UsageLegendRow tone={colors.purple} label="Rerank runs" value={usage.rerankRuns.toLocaleString()} />
      </View>

      {/* Live footer */}
      <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 13 }} />
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
        <View style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: colors.success }}>
          {visible && Platform.OS === 'web' ? (
            <View style={{ position: 'absolute', top: -2, left: -2, width: 10, height: 10, borderRadius: 999, borderWidth: 1, borderColor: colors.success, ...({ animation: 'sk-pulse 1.8s ease-out infinite' } as object) }} />
          ) : null}
        </View>
        <Text style={{ fontSize: 10, color: colors.ink4, letterSpacing: 0.3 }}>Live · streaming from SK Meter</Text>
      </View>
    </View>
  );
}

export default function Sidebar({ onLogout, compact = false, mobile = false, onNavigate }: SidebarProps) {
  const { theme, isDark, toggleTheme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { user, fullName, role, org } = useSession();
  const active = useActiveView();
  const { hovered: userHovered, hoverProps: userHoverProps } = useHover();
  const { hovered: logoHovered, hoverProps: logoHoverProps } = useHover();
  const { hovered: creditsHovered, hoverProps: creditsHoverProps } = useHover();

  const isRail = compact && !mobile;
  const name = fullName || 'SK Hire user';
  const email = user?.email ?? '';
  const roleText = roleLabel(role) || (role ? role.replace(/_/g, ' ') : 'Member');
  const orgName = org?.name ?? '';

  // Live over SSE: AI-token usage + jobs/candidate/compliance counts (instant).
  const { aiUsage, counts } = useWorkspaceStats();
  const badges: Partial<Record<NavSection, number>> = {
    jobs: counts.totalJobs,
    candidates: counts.candidates,
    compliance: counts.consentExpiring,
  };

  const inPct = aiUsage.totalTokens > 0 ? Math.round((aiUsage.inputTokens / aiUsage.totalTokens) * 100) : 0;

  const go = (key: NavSection) => {
    navigateApp(key);
    onNavigate?.();
  };

  return (
    <View style={[styles.root, { width: isRail ? 64 : 238, backgroundColor: colors.card, borderRightColor: colors.line }, web({ position: 'relative', zIndex: 30 })]}>
      {/* Logo (animated brand block) */}
      <View
        {...(isRail ? {} : logoHoverProps)}
        style={[styles.logo, { borderBottomColor: colors.line, justifyContent: isRail ? 'center' : 'flex-start' }]}
      >
        <View
          style={[
            styles.logoMark,
            { borderRadius: radii.md, backgroundColor: colors.accent },
            web({ transition: 'transform 240ms cubic-bezier(.2,.8,.2,1), box-shadow 240ms ease' }),
            Platform.OS === 'web'
              ? ({
                  backgroundImage: `linear-gradient(135deg, ${colors.accent}, ${colors.accentInk})`,
                  boxShadow: logoHovered ? `0 6px 20px -4px ${colors.accent}aa` : `0 4px 12px -3px ${colors.accent}66`,
                } as object)
              : null,
            !isRail && logoHovered ? web({ transform: 'scale(1.08) rotate(-3deg)' }) : null,
          ]}
        >
          <Text style={{ color: colors.onAccent, fontWeight: fontWeight.bold, fontSize: 13, letterSpacing: -0.3 }}>SK</Text>
        </View>
        {!isRail && (
          <View style={{ minWidth: 0 }}>
            <Text
              style={[
                { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.4 },
                Platform.OS === 'web'
                  ? ({
                      backgroundImage: `linear-gradient(180deg, ${colors.ink}, ${colors.ink3})`,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      transition: 'filter 240ms ease',
                      filter: logoHovered ? 'brightness(1.25)' : 'brightness(1)',
                    } as object)
                  : null,
              ]}
            >
             SK HIRE
            </Text>
            <View
              style={[
                {
                  flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 4,
                  alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3,
                  borderRadius: radii.full, borderWidth: 1, borderColor: colors.accentLine,
                  backgroundColor: colors.accentSoft,
                },
                Platform.OS === 'web'
                  ? web({ transition: 'border-color 240ms ease, filter 240ms ease', filter: logoHovered ? 'brightness(1.15)' : 'brightness(1)' })
                  : null,
              ]}
            >
              <View style={{ width: 5, height: 5, borderRadius: radii.full, backgroundColor: colors.accent }} />
              <Text
                style={{
                  fontFamily: font.mono, fontSize: 9, letterSpacing: 1.2,
                  fontWeight: fontWeight.semibold, color: colors.ink2, textTransform: 'uppercase',
                }}
              >
                Matching engine
              </Text>
            </View>
          </View>
        )}
      </View>

      {/* Nav */}
      <View style={styles.nav}>
        {!isRail && <Text style={[styles.section, { color: colors.ink4 }]}>WORKSPACE</Text>}
        {(isAdminRole(role) ? [...NAV, OPS_NAV] : NAV).map(item => (
          <NavRow key={item.key} item={item} active={active === item.key} compact={isRail} badge={badges[item.key]} onPress={() => go(item.key)} />
        ))}

        {/* Thin separator, then appearance + log out — same row shape, under Ops. */}
        <View style={[styles.navDivider, { backgroundColor: colors.line }, isRail && { marginHorizontal: 14 }]} />
        <SideActionRow
          compact={isRail}
          icon={isDark ? 'moon' : 'sun'}
          label={isDark ? 'Dark mode' : 'Light mode'}
          onPress={toggleTheme}
          trailing={
            // Display-only — the row press toggles; a live switch here would
            // double-toggle back.
            <View pointerEvents="none"><Switch checked={isDark} /></View>
          }
        />
        <SideActionRow compact={isRail} icon="logout" label="Log out" onPress={onLogout} danger />

      {/* AI USAGE — its own labelled section, placed directly under Log out
          (inside the nav so it isn't pushed to the bottom by flex). */}
      {!isRail && (
        <View style={styles.usageSection}>
          <View style={[styles.usageDivider, { backgroundColor: colors.line }]} />
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 11, marginBottom: 8 }}>
            <Icon name="zap" size={11} color={colors.ink4} />
            <Text style={[styles.section, { color: colors.ink4, marginBottom: 0 }]}>AI USAGE</Text>
          </View>
          <View style={{ position: 'relative' }} {...creditsHoverProps}>
            {/* Flyout with the full token ledger. On desktop it opens to the
                right of the rail; in the mobile drawer it opens ABOVE the card
                and spans the drawer width so it never overflows off-screen. */}
            <CreditsHoverCard visible={creditsHovered} usage={aiUsage} mobile={mobile} />
          <Pressable
            onPress={() => navigateApp('aiUsage')}
            accessibilityRole="button"
            accessibilityLabel="Open AI usage details"
            style={[
              styles.credits,
              { backgroundColor: colors.paper2, borderColor: creditsHovered ? colors.lineStrong : colors.line, borderRadius: radii.md },
              web({ cursor: 'pointer', transition: 'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms ease, border-color 180ms ease' }),
              Platform.OS === 'web'
                ? ({ boxShadow: creditsHovered ? '0 10px 26px -14px rgba(0,0,0,0.35)' : '0 1px 0 rgba(16,24,40,0.04)' } as object)
                : null,
              creditsHovered ? web({ transform: 'translateY(-2px)' }) : null,
            ]}
          >
            <View style={styles.creditsRow}>
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>AI tokens used</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink2 }}>
                  {aiUsage.totalTokens.toLocaleString()}
                </Text>
                <Icon name="chev-r" size={12} color={creditsHovered ? colors.ink2 : colors.ink4} />
              </View>
            </View>
            {/* input/output split (real proportions) */}
            <View style={{ height: 5, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden', flexDirection: 'row' }}>
              <View style={{ width: `${inPct}%`, backgroundColor: colors.accent }} />
              <View style={{ flex: 1, backgroundColor: colors.teal }} />
            </View>
            <Text style={{ fontSize: 10, color: colors.ink4, marginTop: 6 }}>
              {aiUsage.inputTokens.toLocaleString()} in · {aiUsage.outputTokens.toLocaleString()} out · SK Meter
            </Text>
          </Pressable>
          </View>
        </View>
      )}
      </View>

      {/* User block (with premium hover card) */}
      <View style={{ position: 'relative' }} {...(isRail || mobile ? {} : userHoverProps)}>
        {!isRail && !mobile && (
          <ProfileHoverCard visible={userHovered} name={name} email={email} role={roleText} org={orgName} onLogout={onLogout} />
        )}
        <View
          style={[
            styles.user,
            { borderTopColor: colors.line, justifyContent: isRail ? 'center' : 'space-between', backgroundColor: !isRail && userHovered ? colors.paper2 : 'transparent' },
            web({ transition: 'background 160ms ease' }),
          ]}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <View
              style={[
                styles.avatar,
                { backgroundColor: colors.paper3, borderColor: colors.line },
                web({ transition: 'transform 160ms ease' }),
                !isRail && userHovered ? web({ transform: 'scale(1.06)' }) : null,
              ]}
            >
              <Text style={{ fontSize: 12, fontWeight: fontWeight.bold, color: colors.ink2 }}>{initials(name)}</Text>
            </View>
            {!isRail && (
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text numberOfLines={1} style={{ fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.ink }}>{name}</Text>
                <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink3 }}>{roleText}</Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { height: '100%', borderRightWidth: 1, flexDirection: 'column' },
  logo: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 18, borderBottomWidth: 1 },
  logoMark: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center' },
  nav: { flex: 1, paddingHorizontal: 12, paddingTop: 14, gap: 2 },
  section: { fontSize: 10, letterSpacing: 0.6, fontWeight: '600', paddingHorizontal: 11, marginBottom: 8 },
  navRow: { position: 'relative', flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 9, paddingHorizontal: 11 },
  navRowCompact: { justifyContent: 'center', paddingHorizontal: 0 },
  activeBar: { position: 'absolute', left: 0, top: 10, bottom: 10, width: 3, borderRadius: 3 },
  navIcon: { width: 24, height: 24, alignItems: 'center', justifyContent: 'center' },
  credits: { marginHorizontal: 12, marginBottom: 10, padding: 12, borderWidth: 1 },
  navDivider: { height: 1, marginVertical: 8, marginHorizontal: 11 },
  creditsRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  user: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderTopWidth: 1 },
  avatar: { width: 32, height: 32, borderRadius: 999, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  hoverCard: { position: 'absolute', left: 12, width: 320, bottom: '100%', marginBottom: 10, borderWidth: 1, padding: 16, zIndex: 50 },
  // AI-usage card rises UP from the trigger, anchored to the sidebar's left edge
  // Flies out to the RIGHT of the rail (into content, never over the sidebar),
  // bottom-aligned so it rises toward the top on show.
  creditsFlyout: { position: 'absolute', left: '100%', bottom: 0, marginLeft: 10, width: 320, borderWidth: 1, padding: 16, zIndex: 60 },
  // "AI USAGE" — its own labelled sidebar section (header + divider).
  usageSection: { paddingTop: 12, paddingBottom: 4 },
  usageDivider: { height: 1, marginHorizontal: 12, marginBottom: 12 },
  hoverAvatar: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  creditsGlyph: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
});
